import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Link, useSearchParams } from 'react-router';
import { EmptyState } from '../components/EmptyState';
import { Icon } from '../components/Icon';
import { LevelBadge } from '../components/SecretLevel';
import { useMe } from '../lib/useMe';
import { useUrlState } from '../lib/useUrlState';
import {
  Highlighted,
  TIER_LABEL,
  TIER_PIPS,
  TYPE_META,
  WithheldBanner,
  metaFor,
  whyFor,
} from '../lib/searchShared';
import { useGlobalSearch } from '../lib/useGlobalSearch';
import { relTime } from '../lib/useFreshness';

/**
 * `/search` — the full ranked result set with facets, reached from the palette's
 * "See all N results".
 *
 * Same hook as the palette, so a type count or the withheld number can't mean two
 * things across the two surfaces. The difference is presentation: one flat ranked
 * list of cards instead of a top-4-per-type preview, and the grouping replaced by
 * the facet rail.
 *
 * Mono on identifiers here is the same scoped handoff exception documented in
 * GlobalSearchBar — search-only classes, global policy untouched.
 */

/** The index is rebuilt wholesale by a scheduler job; its last run is the honest
 *  "how old is this?" for every result on the page. */
const FRESHNESS = gql`
  query SearchIndexFreshness {
    jobDefinition(name: "search-reindex") {
      name
      lastRun {
        status
        startedAt
        stats
      }
    }
  }
`;
interface FreshnessData {
  jobDefinition?: { name: string; lastRun?: { status: string; startedAt: string; stats: string } };
}

/** The results page wants the whole set, not a preview. Still a cap — the
 *  resolver clamps at 200 — but typeTotal tells us when we're under it. */
const LIMIT_PER_TYPE = 200;

export function SearchResultsPage() {
  const { me } = useMe();
  const [params] = useSearchParams();
  const q = params.get('q') ?? '';
  // Type filter lives in the URL so a faceted view is a link. useUrlState handles
  // the replace-semantics and drops the key when it returns to the default.
  const [type, setType] = useUrlState('type');

  const s = useGlobalSearch({ value: q, tab: type || 'all', limitPerType: LIMIT_PER_TYPE });
  const fresh = useQuery<FreshnessData>(FRESHNESS, { fetchPolicy: 'cache-first' });

  const openPalette = () => {
    // The palette owns ⌘K; synthesizing the event is what keeps "Refine" and the
    // shortcut on exactly one code path.
    window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true, bubbles: true }));
  };

  const tabs = [
    { key: '', label: 'All types', n: s.total, color: 'var(--muted)' },
    ...TYPE_META.map((m) => ({ key: m.type, label: m.label, n: s.counts[m.type] ?? 0, color: m.color })),
  ];

  const lastRun = fresh.data?.jobDefinition?.lastRun;
  const indexedDocs = (() => {
    try {
      const n = JSON.parse(lastRun?.stats || '{}')?.indexed;
      return typeof n === 'number' ? n : null;
    } catch {
      return null;
    }
  })();

  return (
    <div className="page search-page">
      <div className="search-page__head">
        <div>
          <h1 className="search-page__title">
            {q ? <>Results for “{q}”</> : 'Search'}
          </h1>
          <p className="search-page__summary">
            {s.idle
              ? 'Type at least two characters. Repo paths, cluster names, namespaces, service names, env-var names and secret key names are indexed.'
              : `${s.total} result${s.total === 1 ? '' : 's'} across ${
                  Object.keys(s.counts).length
                } entity type${
                  Object.keys(s.counts).length === 1 ? '' : 's'
                } · ordered by where the term matched, then by most recent activity`}
          </p>
        </div>
        <button className="search-refine" onClick={openPalette}>
          <Icon name="search" size={14} />
          Refine
          <kbd>⌘K</kbd>
        </button>
      </div>

      {s.error ? (
        <>
          <p className="error">Couldn&apos;t run the search — the API didn&apos;t respond.</p>
          <p className="muted">
            Nothing is wrong with the index itself; this page only reads it. Retry, then ask in
            #devops-infra.
          </p>
        </>
      ) : (
        <div className="search-page__grid">
          <aside className="search-rail">
            <div>
              <div className="search-rail__label">Entity type</div>
              <div className="search-rail__rows">
                {tabs.map((t) => (
                  <button
                    key={t.key || 'all'}
                    className={`search-facet${(type || '') === t.key ? ' search-facet--on' : ''}${
                      t.n === 0 && t.key ? ' search-facet--zero' : ''
                    }`}
                    onClick={() => setType(t.key)}
                  >
                    <span className="search-facet__dot" style={{ background: t.color }} />
                    <span className="search-facet__label">{t.label}</span>
                    <span className="search-facet__n">{t.n}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Read-only: where the match landed. Not clickable because "matched in
                the subtitle" isn't a filter anyone asks for — it's a reason to
                trust or distrust the row you're already looking at. */}
            <div>
              <div className="search-rail__label">Matched in</div>
              <div className="search-rail__rows">
                {[
                  { label: 'Name', n: s.fieldCounts.name },
                  { label: 'Key / env var', n: s.fieldCounts.body },
                  { label: 'Context only', n: s.fieldCounts.context },
                  // The handoff's three buckets miss the query's third OR branch:
                  // a trigram near-miss where the term is in none of the fields.
                  // Folding those into "Context only" would claim the subtitle
                  // matched when it didn't. Covers tier 1 (all words present but
                  // not adjacent) and tier 0 (nothing literal matched) — the pips
                  // and the row chip tell those two apart.
                  { label: 'Token / similar only', n: s.fieldCounts.similar },
                ].map((f) => (
                  <div key={f.label} className="search-facet search-facet--static">
                    <span className="search-facet__label">{f.label}</span>
                    <span className="search-facet__n">{f.n}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="search-fresh">
              <div className="search-fresh__title">Index freshness</div>
              <div className="search-fresh__body">
                {lastRun?.startedAt ? (
                  <>
                    Rebuilt from source {relTime(Date.now() - new Date(lastRun.startedAt).getTime())}{' '}
                    ago by the <code>search-reindex</code> job.
                  </>
                ) : (
                  <>
                    The <code>search-reindex</code> job has no recorded run — results may be stale.
                  </>
                )}
              </div>
              {indexedDocs != null && (
                <div className="search-fresh__count">{indexedDocs.toLocaleString()} documents</div>
              )}
            </div>
          </aside>

          <div className={`search-cards${s.loading && s.flat.length > 0 ? ' is-stale' : ''}`}>
            <WithheldBanner n={s.withheld} levels={me?.allowedLevels} />

            {s.idle ? (
              <EmptyState
                icon="search"
                title="Nothing searched yet"
                body="Press ⌘K anywhere, or type at least two characters. Secret key names are indexed, so searching MONGO_SCIO_URL finds the bundle that contains it."
              />
            ) : s.flat.length === 0 ? (
              <EmptyState
                icon="search"
                title={
                  s.total === 0
                    ? `No match for “${s.term}”`
                    : `No ${metaFor(type).label.toLowerCase()} match “${s.term}”`
                }
                body={
                  s.total === 0
                    ? 'Repo paths, cluster names, namespaces, service names, env-var names and secret key names are indexed. Values are never indexed.'
                    : `${s.total} results exist in other entity types — clear the type filter to see them.`
                }
              />
            ) : (
              s.flat.map((h) => {
                const meta = metaFor(h.entityType);
                const why = whyFor(h, s.term);
                return (
                  <Link
                    key={`${h.entityType}:${h.entityId}`}
                    to={h.route}
                    className="search-card"
                  >
                    {/* Was a numeric relevance bar at `rank × 100%`. The score it
                        drew turned out to be constant — 100% of `optisigns` and
                        `k8s` hits shared one value — so the bar implied precision
                        that did not exist. It now shows the tier, which is the
                        thing actually being sorted on: 5 pips = exact title,
                        1 = token only, 0 = fuzzy near-miss. */}
                    <span className="search-card__rank">
                      <span className="search-card__type" style={{ color: meta.color }}>
                        {meta.short}
                      </span>
                      <span
                        className="hit-tier"
                        title={TIER_LABEL[h.tier] ?? 'match'}
                        aria-label={`match quality: ${TIER_LABEL[h.tier] ?? 'match'}`}
                      >
                        {TIER_PIPS.map((n) => (
                          <span
                            key={n}
                            className={`hit-tier__pip${h.tier >= n ? ' hit-tier__pip--on' : ''}`}
                            style={h.tier >= n ? { background: meta.color } : undefined}
                          />
                        ))}
                      </span>
                    </span>

                    <span className="search-card__main">
                      <span className="search-card__title">
                        <Highlighted text={h.title} tokens={s.marks} />
                      </span>
                      <span className="search-card__sub">
                        {h.subtitle && <Highlighted text={h.subtitle} tokens={s.marks} />}
                        {h.subtitle && <span className="search-card__sep"> · </span>}
                        <code className="search-card__route">{h.route}</code>
                      </span>
                      {why && (
                        <span className={`hit-why hit-why--${why.kind}`}>
                          <span className="hit-why__label">{why.label}</span>
                          {why.value && (
                            <span className="hit-why__value">
                              <Highlighted text={why.value} tokens={s.marks} />
                            </span>
                          )}
                        </span>
                      )}
                    </span>

                    {h.level && (
                      <span className="search-card__level">
                        <LevelBadge level={h.level} />
                      </span>
                    )}
                  </Link>
                );
              })
            )}

            {/* The resolver caps each type at 200. typeTotal is the true number, so
                we can say precisely when we are showing less than everything —
                rather than the old vague "more exist". */}
            {!s.idle &&
              s.flat.length > 0 &&
              Object.entries(s.counts).some(([, n]) => n >= LIMIT_PER_TYPE) && (
                <p className="muted text-xs">
                  Some types hit the {LIMIT_PER_TYPE}-result cap — narrow the search or filter by
                  type to reach the rest.
                </p>
              )}
          </div>
        </div>
      )}
    </div>
  );
}
